/**
 * Weather Filters Component
 *
 * Sidebar filters for country, climate zone, and source type.
 * Supports a "pending" mode where changes are staged until applied.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ASHRAEClimateZone, WeatherFileSourceType } from '../../types';
import { ASHRAE_CLIMATE_ZONES } from '../../types';

export interface WeatherFiltersProps {
  countries: { code: string; name: string; count: number }[];
  climateZones: { zone: ASHRAEClimateZone; count: number }[];
  sourceTypes: { type: WeatherFileSourceType; count: number }[];
  yearRanges: { yearRange: string; count: number }[];
  selectedCountry: string | null;
  selectedClimateZones: ASHRAEClimateZone[];
  selectedSourceTypes: WeatherFileSourceType[];
  selectedYearRanges: string[];
  onCountryChange: (country: string | null) => void;
  onClimateZonesChange: (zones: ASHRAEClimateZone[]) => void;
  onSourceTypesChange: (types: WeatherFileSourceType[]) => void;
  onYearRangesChange: (ranges: string[]) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  // For pending/apply mode
  pendingMode?: boolean;
  onApply?: () => void;
  onCancel?: () => void;
  // For getting facet counts based on pending selections
  onPendingChange?: (pending: PendingFilters) => void;
  // Preview result count for pending filters
  pendingResultCount?: number | undefined;
}

export interface PendingFilters {
  country: string | null;
  climateZones: ASHRAEClimateZone[];
  sourceTypes: WeatherFileSourceType[];
  yearRanges: string[];
}

export function WeatherFilters({
  countries,
  climateZones,
  sourceTypes,
  yearRanges,
  selectedCountry,
  selectedClimateZones,
  selectedSourceTypes,
  selectedYearRanges,
  onCountryChange,
  onClimateZonesChange,
  onSourceTypesChange,
  onYearRangesChange,
  onClearFilters,
  hasActiveFilters,
  pendingMode = false,
  onApply,
  onCancel,
  onPendingChange,
  pendingResultCount,
}: WeatherFiltersProps) {
  // Internal pending state for apply-on-click mode
  const [pendingCountry, setPendingCountry] = useState<string | null>(selectedCountry);
  const [pendingClimateZones, setPendingClimateZones] = useState<ASHRAEClimateZone[]>(selectedClimateZones);
  const [pendingSourceTypes, setPendingSourceTypes] = useState<WeatherFileSourceType[]>(selectedSourceTypes);
  const [pendingYearRanges, setPendingYearRanges] = useState<string[]>(selectedYearRanges);

  // Scroll state for climate zone filter
  const climateZoneScrollRef = useRef<HTMLDivElement>(null);
  const [showScrollFade, setShowScrollFade] = useState(false);

  // Check if climate zone list can scroll and update fade indicator
  const updateScrollFade = useCallback(() => {
    const el = climateZoneScrollRef.current;
    if (el) {
      const canScrollMore = el.scrollHeight > el.clientHeight &&
        el.scrollTop + el.clientHeight < el.scrollHeight - 4;
      setShowScrollFade(canScrollMore);
    }
  }, []);

  // Update fade on mount and when climate zones change
  useEffect(() => {
    updateScrollFade();
  }, [climateZones, updateScrollFade]);

  // Reset pending state when applied filters change (e.g., from parent or on mount)
  useEffect(() => {
    setPendingCountry(selectedCountry);
    setPendingClimateZones(selectedClimateZones);
    setPendingSourceTypes(selectedSourceTypes);
    setPendingYearRanges(selectedYearRanges);
  }, [selectedCountry, selectedClimateZones, selectedSourceTypes, selectedYearRanges]);

  // Notify parent of pending changes for facet count updates
  useEffect(() => {
    if (pendingMode && onPendingChange) {
      onPendingChange({
        country: pendingCountry,
        climateZones: pendingClimateZones,
        sourceTypes: pendingSourceTypes,
        yearRanges: pendingYearRanges,
      });
    }
  }, [pendingMode, onPendingChange, pendingCountry, pendingClimateZones, pendingSourceTypes, pendingYearRanges]);

  // Use pending values in pending mode, otherwise use actual values
  const currentCountry = pendingMode ? pendingCountry : selectedCountry;
  const currentClimateZones = pendingMode ? pendingClimateZones : selectedClimateZones;
  const currentSourceTypes = pendingMode ? pendingSourceTypes : selectedSourceTypes;
  const currentYearRanges = pendingMode ? pendingYearRanges : selectedYearRanges;

  const handleCountryChange = useCallback((country: string | null) => {
    if (pendingMode) {
      setPendingCountry(country);
    } else {
      onCountryChange(country);
    }
  }, [pendingMode, onCountryChange]);

  const toggleClimateZone = useCallback((zone: ASHRAEClimateZone) => {
    if (pendingMode) {
      setPendingClimateZones(prev =>
        prev.includes(zone) ? prev.filter((z) => z !== zone) : [...prev, zone]
      );
    } else {
      if (selectedClimateZones.includes(zone)) {
        onClimateZonesChange(selectedClimateZones.filter((z) => z !== zone));
      } else {
        onClimateZonesChange([...selectedClimateZones, zone]);
      }
    }
  }, [pendingMode, selectedClimateZones, onClimateZonesChange]);

  const toggleSourceType = useCallback((type: WeatherFileSourceType) => {
    if (pendingMode) {
      setPendingSourceTypes(prev =>
        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
      );
    } else {
      if (selectedSourceTypes.includes(type)) {
        onSourceTypesChange(selectedSourceTypes.filter((t) => t !== type));
      } else {
        onSourceTypesChange([...selectedSourceTypes, type]);
      }
    }
  }, [pendingMode, selectedSourceTypes, onSourceTypesChange]);

  const toggleYearRange = useCallback((range: string) => {
    if (pendingMode) {
      setPendingYearRanges(prev =>
        prev.includes(range) ? prev.filter((r) => r !== range) : [...prev, range]
      );
    } else {
      if (selectedYearRanges.includes(range)) {
        onYearRangesChange(selectedYearRanges.filter((r) => r !== range));
      } else {
        onYearRangesChange([...selectedYearRanges, range]);
      }
    }
  }, [pendingMode, selectedYearRanges, onYearRangesChange]);

  const handleApply = useCallback(() => {
    onCountryChange(pendingCountry);
    onClimateZonesChange(pendingClimateZones);
    onSourceTypesChange(pendingSourceTypes);
    onYearRangesChange(pendingYearRanges);
    onApply?.();
  }, [pendingCountry, pendingClimateZones, pendingSourceTypes, pendingYearRanges, onCountryChange, onClimateZonesChange, onSourceTypesChange, onYearRangesChange, onApply]);

  const handleCancel = useCallback(() => {
    // Reset pending state to current applied values
    setPendingCountry(selectedCountry);
    setPendingClimateZones(selectedClimateZones);
    setPendingSourceTypes(selectedSourceTypes);
    setPendingYearRanges(selectedYearRanges);
    onCancel?.();
  }, [selectedCountry, selectedClimateZones, selectedSourceTypes, selectedYearRanges, onCancel]);

  const handleClear = useCallback(() => {
    if (pendingMode) {
      setPendingCountry(null);
      setPendingClimateZones([]);
      setPendingSourceTypes([]);
      setPendingYearRanges([]);
    } else {
      onClearFilters();
    }
  }, [pendingMode, onClearFilters]);

  // Check if there are pending changes (different from applied)
  const hasPendingChanges = pendingMode && (
    pendingCountry !== selectedCountry ||
    JSON.stringify([...pendingClimateZones].sort()) !== JSON.stringify([...selectedClimateZones].sort()) ||
    JSON.stringify([...pendingSourceTypes].sort()) !== JSON.stringify([...selectedSourceTypes].sort()) ||
    JSON.stringify([...pendingYearRanges].sort()) !== JSON.stringify([...selectedYearRanges].sort())
  );

  // Check if pending filters have any selection
  const hasPendingFilters = pendingMode && (
    pendingCountry !== null ||
    pendingClimateZones.length > 0 ||
    pendingSourceTypes.length > 0 ||
    pendingYearRanges.length > 0
  );

  const showClearButton = pendingMode ? hasPendingFilters : hasActiveFilters;

  return (
    <div className="space-y-6">
      {/* Clear filters button */}
      {showClearButton && (
        <button
          onClick={handleClear}
          className="text-sm text-primary hover:underline"
        >
          Clear all filters
        </button>
      )}

      {/* Country filter */}
      <div>
        <h3 className="mb-2 text-sm font-medium">Country</h3>
        <select
          value={currentCountry ?? ''}
          onChange={(e) => handleCountryChange(e.target.value || null)}
          className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All countries</option>
          {countries.map(({ code, name, count }) => (
            <option key={code} value={code}>
              {name} ({count})
            </option>
          ))}
        </select>
      </div>

      {/* Climate zone filter */}
      <div>
        <h3 className="mb-2 text-sm font-medium">Climate Zone (ASHRAE)</h3>
        <div className="relative">
          <div
            ref={climateZoneScrollRef}
            onScroll={updateScrollFade}
            className="max-h-48 space-y-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40"
          >
            {climateZones
              .filter(({ count }) => count > 0)
              .map(({ zone, count }) => {
                const zoneInfo = ASHRAE_CLIMATE_ZONES[zone];
                return (
                  <label
                    key={zone}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={currentClimateZones.includes(zone)}
                      onChange={() => toggleClimateZone(zone)}
                      className="rounded border-gray-300"
                    />
                    <span
                      className="flex-1 truncate text-sm"
                      title={zoneInfo?.description}
                    >
                      {zone} - {zoneInfo?.name ?? zone}
                    </span>
                    <span className="text-xs text-muted-foreground">{count}</span>
                  </label>
                );
              })}
          </div>
          {/* Fade indicator when content overflows */}
          {showScrollFade && (
            <div
              className="pointer-events-none absolute bottom-0 left-0 right-1 h-6 bg-gradient-to-t from-background to-transparent"
              aria-hidden="true"
            />
          )}
        </div>
      </div>

      {/* Source type filter */}
      <div>
        <h3 className="mb-2 text-sm font-medium">Data Source</h3>
        <div className="space-y-1">
          {sourceTypes
            .filter(({ count }) => count > 0)
            .map(({ type, count }) => (
              <label
                key={type}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={currentSourceTypes.includes(type)}
                  onChange={() => toggleSourceType(type)}
                  className="rounded border-gray-300"
                />
                <span className="flex-1 text-sm">{type}</span>
                <span className="text-xs text-muted-foreground">{count}</span>
              </label>
            ))}
        </div>
      </div>

      {/* Year range filter */}
      <div>
        <h3 className="mb-2 text-sm font-medium">Year Range</h3>
        <div className="space-y-1">
          {yearRanges
            .filter(({ count }) => count > 0)
            .map(({ yearRange, count }) => (
              <label
                key={yearRange}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={currentYearRanges.includes(yearRange)}
                  onChange={() => toggleYearRange(yearRange)}
                  className="rounded border-gray-300"
                />
                <span className="flex-1 text-sm">
                  {yearRange === 'none' ? 'No year range' : yearRange}
                </span>
                <span className="text-xs text-muted-foreground">{count}</span>
              </label>
            ))}
        </div>
      </div>

      {/* Apply/Cancel buttons for pending mode */}
      {pendingMode && (
        <div className="flex gap-2 border-t pt-4">
          <button
            onClick={handleApply}
            disabled={!hasPendingChanges}
            className="flex-1 rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {pendingResultCount !== undefined ? (
              <>
                Apply
                <span className="ml-1.5 rounded-full bg-primary-foreground/20 px-2 py-0.5 text-xs">
                  {pendingResultCount.toLocaleString()} results
                </span>
              </>
            ) : (
              'Apply Filters'
            )}
          </button>
          <button
            onClick={handleCancel}
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Legend - only show when not in pending mode (to save space in popovers) */}
      {!pendingMode && (
        <div className="border-t pt-4">
          <h3 className="mb-2 text-sm font-medium">Climate Zone Legend</h3>
          <div className="space-y-1 text-xs text-muted-foreground">
            <div><span className="font-medium">0:</span> Extremely Hot</div>
            <div><span className="font-medium">1:</span> Very Hot</div>
            <div><span className="font-medium">2:</span> Hot</div>
            <div><span className="font-medium">3:</span> Warm</div>
            <div><span className="font-medium">4:</span> Mixed</div>
            <div><span className="font-medium">5:</span> Cool</div>
            <div><span className="font-medium">6:</span> Cold</div>
            <div><span className="font-medium">7:</span> Very Cold</div>
            <div><span className="font-medium">8:</span> Subarctic</div>
            <div className="mt-2">
              <span className="font-medium">A:</span> Humid |{' '}
              <span className="font-medium">B:</span> Dry |{' '}
              <span className="font-medium">C:</span> Marine
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
